<template>
  <div>
    <div class="monthNavWrap">
      <button @click="prevMonth" class="monthNav">◀︎</button>
      <h2>{{ monthName }} {{ selectedYear }}</h2>
      <button @click="nextMonth" class="monthNav">▶︎</button>
    </div>
    <div class="summaryData" style="margin: 1rem 0 0">
      <div>
        <p>Days in office</p>
        <p>{{ daysInOfficeCount }} days</p>
      </div>
      <div>
        <p>Total overtime hours</p>
        <p>{{ monthlyOvertimeDisplay }} hours</p>
      </div>
      <div>
        <p>17-week avg overtime</p>
        <p>
          {{ seventeenWeekAverageDisplay }} hours
          <span v-if="isSeventeenWeekPartial">*</span>
        </p>
      </div>
      <div v-if="isMonthClosed">
        <p>Overtime after applying FlexiTime</p>
        <p>{{ overtimeAfterFlexiTime }} hours</p>
      </div>
    </div>
    <p v-if="isSeventeenWeekPartial" class="summaryNote">
      * Averaged over {{ seventeenWeekDivisorWeeks }} weeks
    </p>

    <div class="weekendSwc">
      <span>Weekends</span>
      <input
        type="checkbox"
        id="hide-toggle"
        v-model="hideWeekends"
        class="weekendCheck"
      />
      <label for="hide-toggle" class="weekendLabel"></label>
    </div>

    <ConfirmationModal
      :visible="showConfirmationModal"
      :message="`Are you sure you want to ${
        isMonthClosed ? 'unlock' : 'close'
      } this month and send your attendance data to HR? Once you close this month, you will no longer be able to change your attendance data.`"
      @confirm="confirmToggleMonthClosed"
      @cancel="cancelToggleMonthClosed"
    >
        <div v-if="!isMonthClosed" class="flexi-time-section">
            <p>Total overtime for {{ monthName }} {{ selectedYear }}: <strong>{{ monthlyOvertimeDisplay }} hours</strong></p>
            <div class="form-group">
                <label for="flexitime-hours">Hours to convert to Flexi Time:</label>
                <input type="number" id="flexitime-hours" v-model.number="flexiHours" min="0" :max="parseFloat(monthlyOvertimeDisplay)" step="0.25">
            </div>
        </div>
    </ConfirmationModal>

    <div v-if="loading" v-motion-fade :delay="100">Loading...</div>
    <div v-else-if="error" style="color: red">{{ error }}</div>
    <div v-else-if="scheduleData" class="tableWrap">
      <table v-motion-fade>
        <thead>
          <tr>
            <th class="bgBlu"></th>
            <template v-if="showSelfCols">
                <th class="bgBlu">Start Time</th>
                <th class="bgBlu">End Time</th>
                <th class="bgBlu">Lunch</th>
                <th class="bgBlu">Overtime</th>
                <th class="bgBlu">Location</th>
                <th class="bgBlu">Holiday</th>
                <th class="bgBlu">OT Reason</th>
                <th class="bgBlu" v-if="!isTopLevel">OT Approver</th>
            </template>
            <th class="bgBlu">Seats Free</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="day in scheduleData.days" :key="day">
            <tr
              v-if="
                !(
                  hideWeekends &&
                  (isSaturday(day) || isSunday(day) || isUKHoliday(day))
                )
              "
              :class="{
                today: isToday(day),
                saturday: isSaturday(day),
                sunday: isSunday(day),
                'uk-holiday': isUKHoliday(day),
              }"
            >
              <td>
                {{ getDayLabel(day) }}
                <span class="days">{{ getWeekday(day) }}</span>
              </td>

              <!-- Login user's attendance information -->
              <template v-if="showSelfCols && currentUserId">
                <template v-for="user in sortedUsers" :key="user.id">
                  <template v-if="user.id === currentUserId">
                    <td class="noWrapFlex">
                      <select
                        :id="`clock_in-hour-${day}`"
                        style="width: 55px; padding: 0.4rem;"
                        @change="handleTimeChange(day, 'clock_in')"
                        :value="getHour(getAttendance(day).clock_in)"
                        :disabled="isFuture(day) || isMonthClosed || getAttendance(day).work_location === 'Business Trip' || isFullDayLeave(day)"
                      >
                        <option value="">--</option>
                        <option v-for="h in hours" :key="h" :value="h" :disabled="isStartTimeHourDisabled(h, day)">{{ h }}</option>
                      </select>
                      <span>:</span>
                      <select
                        :id="`clock_in-minute-${day}`"
                        style="width: 55px; padding: 0.4rem;"
                        @change="handleTimeChange(day, 'clock_in')"
                        :value="getMinute(getAttendance(day).clock_in)"
                        :disabled="isFuture(day) || isMonthClosed || getAttendance(day).work_location === 'Business Trip' || isFullDayLeave(day)"
                      >
                        <option value="">--</option>
                        <option
                          v-for="m in getMinuteOptions(day, getAttendance(day).clock_in)"
                          :key="m"
                          :value="m"
                        >
                          {{ m }}
                        </option>
                      </select>
                    </td>
                    <td class="noWrapFlex">
                      <select
                        :id="`clock_out-hour-${day}`"
                        style="width: 55px; padding: 0.4rem;"
                        @change="handleTimeChange(day, 'clock_out')"
                        :value="getHour(getAttendance(day).clock_out)"
                        :disabled="isFuture(day) || isMonthClosed || getAttendance(day).work_location === 'Business Trip' || isFullDayLeave(day)"
                      >
                        <option value="">--</option>
                        <option v-for="h in endTimeHours" :key="h" :value="h" :disabled="isEndTimeHourDisabled(h, day)">{{ h }}</option>
                      </select>
                      <span>:</span>
                      <select
                        :id="`clock_out-minute-${day}`"
                        style="width: 55px; padding: 0.4rem;"
                        @change="handleTimeChange(day, 'clock_out')"
                        :value="getMinute(getAttendance(day).clock_out)"
                        :disabled="isFuture(day) || isMonthClosed || getAttendance(day).work_location === 'Business Trip' || isFullDayLeave(day)"
                      >
                        <option value="">--</option>
                        <option
                          v-for="m in getMinuteOptions(day, getAttendance(day).clock_out)"
                          :key="m"
                          :value="m"
                        >
                          {{ m }}
                        </option>
                      </select>
                    </td>

                    <td>
                      <select
                        :value="getLunchSelectValue(day)"
                        @change="onLunchChange(day, $event.target.value)"
                        :disabled="isFuture(day) || isMonthClosed || getAttendance(day).work_location === 'Business Trip' || isFullDayLeave(day)"
                      >
                        <option v-for="m in lunchOptions" :key="m" :value="m">
                          {{ m }} min
                        </option>
                        <option v-if="isExpat" value="NLA">NLA</option>
                      </select>
                    </td>
                    <td class="overTime">
                      {{ getOvertimeDisplay(day) }}
                    </td>
                    <td class="location">
                      <select
                        :value="getAttendance(day).work_location"
                        @change="updateWorkLocation(day, $event)"
                        :disabled="isFuture(day) || isMonthClosed || isFullDayLeave(day)"
                      >
                        <option value="">-</option>
                        <option v-for="loc in workLocations" :key="loc" :value="loc">
                          {{ loc }}
                        </option>
                      </select>
                    </td>
                    <td>
                        {{ getLeaveForUser(currentUserId, day) }}
                    </td>
                    <td>
                      <textarea
                        :value="getAttendance(day).ot_reason"
                        @change="updateOvertimeDetails(day, 'ot_reason', $event.target.value)"
                        :disabled="isFullDayLeave(day)"
                        rows="2"
                        style="width: 100%; resize: vertical;"
                      ></textarea>
                    </td>
                    <td v-if="!isTopLevel">
                      <select
                        :value="getAttendance(day).ot_approver"
                        @change="updateOvertimeDetails(day, 'ot_approver', $event.target.value)"
                        @focus="fetchApprovers"
                        :disabled="!hasOvertime(day)"
                        style="width: 120px;"
                      >
                        <option v-if="!approverList || approverList.length === 0" value="" disabled>Loading...</option>
                        <option v-else value="">- Select -</option>
                        <option v-for="approver in approverList"
                                :key="approver.id"
                                :value="approver.username">
                            {{ approver.username }}
                        </option>
                      </select>
                    </td>
                  </template>
                </template>
              </template>

              <td
                class="seat-cell"
              >
                <template v-if="hasSeatReservation(currentUserId, day)">
                    <span class="reservation bgBlu">
                      <a href="#" @click.prevent="goToReserveView(day)">
                        {{ getSeatReservation(currentUserId, day).seat_number }}
                      </a>
                    </span>
                </template>
                <template v-else>
                    <div v-if="canReserve(day)">
                        {{ getAvailableSeatsCount(day) }}
                        <a :href="`/attendance/deskreservation?date=${day}`" class="reserveLink">Reserve</a>
                    </div>
                </template>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
          <button
        @click="handleToggleMonthClosed"
        :disabled="isMonthClosed"
        class="submit-button"
      >
        {{ isMonthClosed ? "Submitted" : "Submit Attendance to Your Manager" }}
      </button>
  </div>
</template>

<script>
    import ConfirmationModal from "../components/ConfirmationModal.vue";
    import { onMounted } from "vue";
    import api, {
        setAuthToken,
        fetchTeams,
        fetchSchedule,
        fetchHolidays,
        clockIn,
        clockOut,
        fetchAttendance,
        fetchSeatLabels,
        fetchProfile,
    } from "../services/api.js";

    const MONTH_FIELD = {
        1: "apr",
        2: "may",
        3: "jun",
        4: "jul",
        5: "aug",
        6: "sep",
        7: "oct",
        8: "nov",
        9: "dec",
        10: "jan",
        11: "feb",
        12: "mar",
    };
    const NEW_SEAT_LAYOUT_START = "2026-06-02";
    const NEW_LAYOUT_TOTAL_SEATS = 17;

    export default {
        name: "CalendarView",
        components: {
            ConfirmationModal,
        },
        data() {
            const today = this.$now();
            return {
                showConfirmationModal: false,
                scheduleData: null,
                seatReservations: [],
                loading: false,
                error: "",
                selectedYear: today.getFullYear(),
                selectedMonth: today.getMonth() + 1,
                selectedDay: 1,
                attendanceRecords: [],
                lunchOptions: [0, 30, 45, 60],
                workLocations: ["Office", "Home", "Business Trip"],
                isExpat: false,
                hideWeekends: true,
                compensatoryBalanceDays: 0,
                annualOvertime: null,
                seventeenWeekAverageMinutes: 0,
                seventeenWeekDivisorWeeks: 17,
                isSeventeenWeekPartial: false,
                ukHolidays: [],
                halfHolidays: [],
                companyHolidayNameByDate: {},
                companyHolidayPeriodByDate: {},
                isMonthClosed: false,
                isAttendanceAdmin: false,
                isTopLevel: false,
                totalSeats: 0,
                flexiHours: 0,
                approverList: [],
            };
        },
        computed: {
            hours() {
              const options = [];
              for (let i = 8; i <= 23; i++) {
                options.push(String(i).padStart(2, "0"));
              }
              return options;
            },
            endTimeHours() {
              return [...this.hours, "00", "01", "02", "03", "04", "05"];
            },
            showSelfCols() {
              // 管理者ならtheadの対象部分を非表示にする
              return Array.isArray(this.sortedUsers)
                  ? this.sortedUsers.some(u => u.id === this.currentUserId)
                  : false;
            },
            myReservation() {
                return this.seatReservations.find(
                    (r) => r.user === this.currentUserId && !r.is_guest
                    );
            },
            currentUserId() {
                return Number(localStorage.getItem("userId"));
            },
            sortedUsers() {
                if (!this.scheduleData || !Array.isArray(this.scheduleData.users)) {
                    return [];
                }
                const me = this.scheduleData.users.find(
                    (u) => u.id === this.currentUserId
                );
                return me ? [me] : [];
            },
            reservationsList() {
                const data = this.scheduleData;
                if (Array.isArray(data)) {
                    return data;
                } else if (data && Array.isArray(data.reservations)) {
                    return data.reservations;
                } else if (
                    data &&
                    typeof data === "object" &&
                    Object.keys(data).length > 0
                    ) {
                    return [data];
                } else {
                    return [];
                }
            },
            computedSelectedDate() {
                const monthStr = this.selectedMonth.toString().padStart(2, "0");
                const dayStr = this.selectedDay.toString().padStart(2, "0");
                return `${this.selectedYear}-${monthStr}-${dayStr}`;
            },
            attendanceMap() {
                const map = {};
                this.attendanceRecords.forEach((rec) => {
                    const key = `${rec.date}|${rec.user || this.currentUserId}`;
                    map[key] = rec;
                });
                return map;
            },
            userSeatReservations() {
                return this.seatReservations.filter((r) => {
                    if (r.user !== this.currentUserId) return false;
                    const d = new Date(r.date);
                    return (
                        d.getFullYear() === this.selectedYear &&
                        d.getMonth() + 1 === this.selectedMonth
                        );
                });
            },
            totalWorkDaysByReservation() {
                const uniqueDates = new Set(this.userSeatReservations.map((r) => r.date));
                return uniqueDates.size;
            },
            daysInOfficeCount() {
                return this.userAttendanceRecords.filter(
                    (rec) => rec.work_location === 'Office'
                ).length;
            },
            userAttendanceRecords() {
                return this.attendanceRecords.filter((rec) => {
                    if (rec.user !== this.currentUserId) return false;
                    const d = new Date(rec.date);
                    return (
                        d.getFullYear() === this.selectedYear &&
                        d.getMonth() + 1 === this.selectedMonth
                        );
                });
            },
            totalOvertimeByAttendance() {
                const totalMin = this.userAttendanceRecords.reduce((sum, rec) => {
                    if (!rec.clock_in || !rec.clock_out) return sum;
                    const inMs = new Date(`${rec.date}T${rec.clock_in}`).getTime();
                    const outMs = new Date(`${rec.date}T${rec.clock_out}`).getTime();
                    const lunchMs = (rec.lunch_minutes || 60) * 60 * 1000;
                    const workMs = outMs - inMs - lunchMs;
                    const thresholdMs = 7.5 * 60 * 60 * 1000; // 7.5時間

                    if (workMs > thresholdMs) {
                        const overMs = workMs - thresholdMs;
                        const overMin = Math.floor(overMs / (60 * 1000));
                        return sum + overMin;
                    }
                    return sum;
                }, 0);

                const h = Math.floor(totalMin / 60);
                const m = totalMin % 60;
                return `${h}:${String(m).padStart(2, "0")}`;
            },
            /** Returns the overtime hours for the target month in "hours" */
            monthlyOvertimeDisplay() {
                if (!this.annualOvertime) return "0";
                const idx =
                this.selectedMonth >= 4
                ? this.selectedMonth - 3
                : this.selectedMonth + 9;
                const minutes = this.annualOvertime[MONTH_FIELD[idx]] || 0;
                return (minutes / 60).toFixed(2);
            },
            seventeenWeekAverageDisplay() {
                return (this.seventeenWeekAverageMinutes / 60).toFixed(2);
            },
            overtimeAfterFlexiTime() {
                const totalOvertime = parseFloat(this.monthlyOvertimeDisplay);
                return (totalOvertime - this.flexiHours).toFixed(2);
            },
            monthName() {
                const monthNames = [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                ];
                return monthNames[this.selectedMonth - 1];
            },
        },
        methods: {
            isMorningLeave(day) {
                const leaveType = this.getLeaveTypeForUser(this.currentUserId, day);
                return leaveType === 'AM';
            },
            isAfternoonLeave(day) {
                const leaveType = this.getLeaveTypeForUser(this.currentUserId, day);
                return leaveType === 'PM';
            },
            isHalfDayLeaveApproved(day) {
                const leaveType = this.getLeaveTypeForUser(this.currentUserId, day);
                return leaveType === 'Morning' || leaveType === 'Afternoon';
            },
            isFullDayLeave(day) {
                const leaveType = this.getLeaveTypeForUser(this.currentUserId, day);
                return leaveType === 'Full';
            },
            handleTimeChange(day, field) {
                const hourEl = document.getElementById(`${field}-hour-${day}`);
                const minuteEl = document.getElementById(`${field}-minute-${day}`);
                if (!hourEl || !minuteEl) {
                    return;
                }

                const hour = hourEl.value;
                const minute = minuteEl.value;

                // If both are empty, it means the user is clearing the time.
                if (!hour && !minute) {
                    this.onTimeEdit(day, field, null);
                    return;
                }

                // If one is empty but not the other, wait for both to be filled.
                if (!hour || !minute) {
                    return;
                }

                const newTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
                
                const attendance = this.getAttendance(day);

                if (field === 'clock_out') {
                    const startTime = attendance.clock_in;
                    if (startTime && this.toComparableMinutes(newTime, true) < this.toComparableMinutes(startTime)) {
                        alert('End Time cannot be earlier than Start Time.');
                        hourEl.value = this.getHour(attendance.clock_out);
                        minuteEl.value = this.getMinute(attendance.clock_out);
                        return;
                    }
                }

                if (field === 'clock_in') {
                    const endTime = attendance.clock_out;
                    if (endTime && this.toComparableMinutes(newTime) > this.toComparableMinutes(endTime, true)) {
                        alert('Start Time cannot be later than End Time.');
                        hourEl.value = this.getHour(attendance.clock_in);
                        minuteEl.value = this.getMinute(attendance.clock_in);
                        return;
                    }
                }

                this.onTimeEdit(day, field, newTime);
            },
            getHour(time) {
              return time ? time.slice(0, 2) : "";
            },
            getMinute(time) {
              return time ? time.slice(3, 5) : "";
            },
            getMinuteOptions(dayStr, currentTime = null) {
                const quarterHourOptions = ["00", "15", "30", "45"];
                const minuteOptions = [];
                for (let i = 0; i <= 59; i++) {
                    minuteOptions.push(String(i).padStart(2, "0"));
                }

                const isQuarterHourModeForDay = dayStr >= "2026-04-17";
                const baseOptions = isQuarterHourModeForDay ? quarterHourOptions : minuteOptions;
                const currentMinute = this.getMinute(currentTime);

                if (currentMinute && !baseOptions.includes(currentMinute)) {
                    return [...baseOptions, currentMinute].sort((a, b) => Number(a) - Number(b));
                }

                return baseOptions;
            },
            toComparableMinutes(time, treatEarlyMorningAsNextDay = false) {
                if (!time) return null;
                const [hourStr, minuteStr] = time.slice(0, 5).split(":");
                let hour = parseInt(hourStr, 10);
                const minute = parseInt(minuteStr, 10);

                if (
                    treatEarlyMorningAsNextDay &&
                    Number.isInteger(hour) &&
                    hour >= 0 &&
                    hour <= 5
                ) {
                    hour += 24;
                }

                return hour * 60 + minute;
            },
            isStartTimeHourDisabled(hour, day) {
                const attendance = this.getAttendance(day);
                const endTime = attendance.clock_out;
                if (!endTime) return false;
                return this.toComparableMinutes(`${hour}:00`) > this.toComparableMinutes(endTime, true);
            },
            isEndTimeHourDisabled(hour, day) {
                const attendance = this.getAttendance(day);
                const startTime = attendance.clock_in;
                if (!startTime) return false;
                return this.toComparableMinutes(`${hour}:00`, true) < this.toComparableMinutes(startTime);
            },
            async updateWorkLocation(day, event) {
                const location = event.target.value;
                const rec = this.getAttendance(day);
                const oldLocation = rec.work_location;

                // Frontend validation: Cannot select 'Business Trip' if half-day leave is approved
                if (location === 'Business Trip' && this.isHalfDayLeaveApproved(day)) {
                    alert("You cannot select 'Business Trip' when a half-day leave is approved.");
                    event.target.value = oldLocation; // Revert UI
                    return;
                }
                // Frontend validation: Cannot select 'Business Trip' if full-day leave is approved
                if (location === 'Business Trip' && this.isFullDayLeave(day)) {
                    alert("You cannot select 'Business Trip' when a full-day leave is approved.");
                    event.target.value = oldLocation; // Revert UI
                    return;
                }

                if (location === 'Business Trip') {
                    if (rec.clock_in || rec.clock_out) {
                        if (!confirm('If marked as a business trip, this will save Clock-in 09:00, Clock-out 17:30, and Lunch 60 minutes. Existing clock times will be overwritten. Continue?')) {
                            event.target.value = oldLocation; // Revert UI
                            return;
                        }
                    }
                    const payload = {
                        work_location: 'Business Trip',
                        clock_in: '09:00:00',
                        clock_out: '17:30:00',
                        lunch_minutes: 60,
                    };
                    if (rec.id) {
                        await api.patch(`attendance/${rec.id}/`, payload);
                    } else {
                        payload.date = day;
                        const res = await api.post("attendance/", payload);
                        this.attendanceRecords.push(res.data);
                    }
                } else {
                    if (rec.id) {
                        await api.patch(`attendance/${rec.id}/`, { work_location: location });
                    } else {
                        const res = await api.post("attendance/", {
                            date: day,
                            work_location: location,
                        });
                        this.attendanceRecords.push(res.data);
                    }
                }
                await this.fetchAttendance(true);
                await this.fetchCompBalance(); // Overtime might change
                if (location === 'Business Trip') {
                    window.location.reload();
                }
            },
            handleToggleMonthClosed() {
                this.flexiHours = 0;
                this.showConfirmationModal = true;
            },
            async confirmToggleMonthClosed() {
                // Validation for flexiHours
                const totalOvertime = parseFloat(this.monthlyOvertimeDisplay);
                if (this.flexiHours < 0) {
                    alert('Flexi Time hours cannot be negative.');
                    return;
                }
                if (this.flexiHours > totalOvertime) {
                    alert(`Flexi Time hours cannot exceed the total overtime of ${totalOvertime} hours.`);
                    return;
                }

                if (this.flexiHours > 0) {
                    const fiscal_year = this.selectedMonth >= 4 ? this.selectedYear : this.selectedYear - 1;
                    const minutes = this.flexiHours * 60;
                    try {
                        await api.post('/attendance/flexitime/', {
                            fiscal_year: fiscal_year,
                            month: this.selectedMonth,
                            minutes: minutes,
                        });
                    } catch (error) {
                        console.error('Failed to submit flexitime:', error);
                        alert('Failed to submit flexitime. Please try again.');
                        this.showConfirmationModal = false;
                        return;
                    }
                }
                this.showConfirmationModal = false;
                await this.toggleMonthClosed();
            },
            cancelToggleMonthClosed() {
                this.showConfirmationModal = false;
            },
            async clockIn() {
                try {
                    await clockIn();
                    alert("Clock In successful!");
                } catch (error) {
                    alert("Clock In failed.");
                }
            },
            async clockOut() {
                try {
                    await clockOut();
                    await this.fetchCompBalance();
                    alert("Clock Out successful!");
                } catch (error) {
                    alert("Clock Out failed.");
                }
            },
            async fetchCompBalance() {
                const res = await api.get("/leave-balance/compbalances/");
                const list = res.data;
                this.compensatoryBalanceDays =
                Array.isArray(list) && list.length ? list[0].balance_days : 0;
            },
            async updateOvertimeDetails(day, field, value) {
                const rec = this.getAttendance(day);
                if (rec && rec.id) {
                    try {
                        await api.patch(`attendance/${rec.id}/`, { [field]: value });
                        await this.fetchAttendance(true); // Re-fetch data to ensure UI is up-to-date
                    } catch (e) {
                        console.error("Failed to update overtime details:", e);
                        alert("Failed to update overtime details.");
                    }
                } else {
                    // This case should be handled by the disabled logic, but as a fallback:
                    alert("Please enter clock-in/out times before adding overtime details, or else your text will not save.");
                }
            },

            hasOvertime(day) {
                const rec = this.getAttendance(day);
                if (!rec || !rec.overtime) {
                    return false;
                }
                const { weekday_overtime, remaining_weekend_holiday_overtime, weekend_holiday_overtime } = rec.overtime;
                // Check if duration string is not null and not '00:00:00'
                const hasPositiveDuration = (duration) => duration && duration.split(':').some(part => parseInt(part, 10) > 0);

                return hasPositiveDuration(weekday_overtime) || 
                       hasPositiveDuration(remaining_weekend_holiday_overtime) || 
                       hasPositiveDuration(weekend_holiday_overtime);
            },

            async onTimeEdit(dayStr, field, newValue) {
                const rawRec = this.attendanceMap[`${dayStr}|${this.currentUserId}`];
                // newValue は "HH:MM" もしくは null を想定。ここでのみ ":00" 付与して一元化
                const normalized = newValue ? `${newValue}:00` : null;

                // Half-day approved: auto-fill clock_out = clock_in + 3h45 when clock_in is set
                if (field === 'clock_in' && normalized && this.isHalfDayLeaveApproved(dayStr)) {
                    const parts = normalized.split(":");
                    const dt = new Date(`${dayStr}T${parts[0]}:${parts[1]}:00`);
                    dt.setMinutes(dt.getMinutes() + 225); // 3h45m
                    const hh = String(dt.getHours()).padStart(2, '0');
                    const mm = String(dt.getMinutes()).padStart(2, '0');
                    const autoClockOut = `${hh}:${mm}:00`;

                    // Apply both clock_in and clock_out in one request when possible
                    const payload = { clock_in: normalized, clock_out: autoClockOut, lunch_minutes: 0 };

                    try {
                        if (rawRec && rawRec.id) {
                            await api.patch(`attendance/${rawRec.id}/`, payload);
                        } else {
                            const createPayload = {
                                date: dayStr,
                                ...payload,
                                lunch_minutes: 0,
                            };
                            const res = await api.post("/attendance/", createPayload);
                            this.attendanceRecords.push(res.data);
                        }
                        await this.fetchAttendance(true);
                        await this.fetchCompBalance();
                        return;
                    } catch (e) {
                        console.error("Failed to auto-fill clock out for half-day:", e);
                        alert("Failed to auto-fill clock out. Please try again.");
                        return;
                    }
                }

                try {
                    if (rawRec && rawRec.id) {
                        // 既存レコードは部分更新（PATCH）。空は必ず null を送る
                        await api.patch(`attendance/${rawRec.id}/`, {
                            [field]: normalized,
                            ...(this.isHalfDayLeaveApproved(dayStr) ? { lunch_minutes: 0 } : {}),
                        });
                    } else {
                        // 新規作成時は date と lunch_minutes を必ず添付
                        const createPayload = {
                            date: dayStr,
                            clock_in: field === "clock_in" ? normalized : null,
                            clock_out: field === "clock_out" ? normalized : null,
                            lunch_minutes: this.isHalfDayLeaveApproved(dayStr) ? 0 : 60,
                            lunch_type: null,
                        };
                        const res = await api.post("/attendance/", createPayload);
                        // 即時UI反映
                        this.attendanceRecords.push(res.data);
                    }
                    await this.fetchAttendance(true);
                    await this.fetchCompBalance();
                } catch (e) {
                    if (e.response && e.response.data) {
                        console.error(
                            "Validation error returned from backend:",
                            e.response.data
                            );
                    } else {
                        console.error("Unexpected error:", e);
                    }
                    alert("Failed to update time. Please check the console for details.");
                }
            },
            async fetchAttendance(forceRefresh = false) {
                try {
                    const res = await fetchAttendance(
                        this.currentUserId,
                        this.selectedYear,
                        this.selectedMonth,
                        forceRefresh
                        );
                    this.attendanceRecords = res.data;
                } catch (e) {
                    console.error("Failed to fetch attendance data", e);
                }
            },
            getAttendance(day) {
                const key = `${day}|${this.currentUserId}`;
                return this.attendanceMap[key] || { lunch_minutes: 60, lunch_type: null };
            },
            getLunchSelectValue(day) {
                const rec = this.getAttendance(day);
                if (rec.lunch_type === 'NLA') return 'NLA';
                return rec.lunch_minutes ?? 60;
            },
            formatTime(time) {
                if (!time) return "–";
            // 先頭 5 文字だけ取り出す
                return time.substr(0, 5);
            },
            getOvertimeDisplay(day) {
                const record = this.getAttendance(day);
                if (!record || !record.overtime) {
                    return "";
                }

                const isHoliday = this.isSaturday(day) || this.isSunday(day) || this.isUKHoliday(day) || this.isHalfHoliday(day);

                if (isHoliday) {
                    const compDays = record.overtime.compensatory_leave_days || 0;
                    const remainingTime = this.formatDuration(record.overtime.remaining_weekend_holiday_overtime);
                    if (compDays > 0) {
                        return `Holiday in Lieu: ${compDays}d / OT: ${remainingTime}`;
                    } else if (record.overtime.weekend_holiday_overtime) {
                        // Display total holiday overtime if no comp leave is generated yet
                        return this.formatDuration(record.overtime.weekend_holiday_overtime);
                    }
                    return "";
                } else {
                    return this.formatDuration(record.overtime.weekday_overtime);
                }
            },
            formatDuration(durationString) {
                if (!durationString) return "0:00";

                // Convert Django DurationField string (including negative like "-1 day, 23:15:00" or "-1 23:17:00") to total seconds
                const parseToSeconds = (value) => {
                    const normalized = value.trim();
                    // Trim any fractional seconds (e.g. ".655889") so parsing works consistently
                    const withoutFraction = normalized.replace(/(\d{1,2}:\d{2}:\d{2})\.\d+/, "$1");

                    // Patterns:
                    // 1) "-1 day, 23:17:00" / "2 days, 01:00:00"
                    const dayPattern = /^(-?\d+)\s+day[s]?,?\s*(\d{1,2}):(\d{2}):(\d{2})$/;
                    // 2) "-1 23:17:00" (SQLite/DRF variant without 'day')
                    const daylessPattern = /^(-?\d+)\s+(\d{1,2}):(\d{2}):(\d{2})$/;
                    // 3) "-00:43:00"
                    const timePattern = /^(-?)(\d{1,2}):(\d{2}):(\d{2})$/;

                    const dayMatch = withoutFraction.match(dayPattern);
                    if (dayMatch) {
                        const [, days, h, m, s] = dayMatch;
                        return Number(days) * 86400 + Number(h) * 3600 + Number(m) * 60 + Number(s);
                    }

                    const daylessMatch = withoutFraction.match(daylessPattern);
                    if (daylessMatch) {
                        const [, days, h, m, s] = daylessMatch;
                        return Number(days) * 86400 + Number(h) * 3600 + Number(m) * 60 + Number(s);
                    }

                    const timeMatch = withoutFraction.match(timePattern);
                    if (timeMatch) {
                        const [, sign, h, m, s] = timeMatch;
                        const baseSeconds = Number(h) * 3600 + Number(m) * 60 + Number(s);
                        return (sign === "-" ? -1 : 1) * baseSeconds;
                    }
                    return null;
                };

                const totalSeconds = parseToSeconds(durationString);
                if (totalSeconds === null || Number.isNaN(totalSeconds)) {
                    return durationString; // fallback: return raw
                }

                const sign = totalSeconds < 0 ? "-" : "";
                const absSeconds = Math.abs(totalSeconds);
                const hours = Math.floor(absSeconds / 3600);
                const minutes = Math.floor((absSeconds % 3600) / 60);
                return `${sign}${hours}:${String(minutes).padStart(2, "0")}`;
            },
            getWeekday(dayStr) {
                const d = new Date(dayStr);
                return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
            },
            async updateLunch(day, minutes) {
                const rec = this.getAttendance(day);
                const isNLA = minutes === 'NLA';
                const payload = {
                    lunch_minutes: isNLA ? 60 : Number(minutes),
                    lunch_type: isNLA ? 'NLA' : null,
                };
                if (rec.id) {
                    await api.patch(`attendance/${rec.id}/`, payload);
                } else {
                    await api.post("attendance/", {
                        date: day,
                        clock_in: null,
                        clock_out: null,
                        ...payload,
                    });
                }
                await this.fetchAttendance(true);
                await this.fetchCompBalance();
                // window.location.reload();
            },
            onLunchChange(day, value) {
                this.updateLunch(day, value);
            },
            async fetchScheduleData() {
                this.loading = true;
                try {
                    const response = await fetchSchedule(
                        this.selectedYear,
                        this.selectedMonth
                        );
                    this.scheduleData = response.data;
                } catch (err) {
                    console.error("Failed to fetch schedule data.", err);
                    this.error = "Failed to fetch schedule data.";
                } finally {
                    this.loading = false;
                }
            },
            getDayLabel(dayStr) {
                const d = new Date(dayStr);
                return d.getDate();
            },
            getLeaveTypeForUser(userId, dayStr) {
                if (!this.scheduleData) return "";
                const record = this.scheduleData.schedules.find(
                    (sch) =>
                    sch.user_id === userId && sch.date === dayStr && sch.type === "DayOff"
                );
                return record?.leave_type || "";
            },
            getCompanyHolidayName(dayStr) {
                return this.companyHolidayNameByDate?.[dayStr] || "";
            },
            isHalfHoliday(dayStr) {
                if (!dayStr) return false;
                const d = new Date(dayStr);
                if (isNaN(d.getTime())) return false;
                const dateStr = d.toISOString().slice(0, 10);
                return this.halfHolidays.includes(dateStr);
            },
            getLeaveForUser(userId, dayStr) {
                const leaveType = this.getLeaveTypeForUser(userId, dayStr);
                let leaveLabel = "";
                switch (leaveType) {
                    case 'Morning': leaveLabel = 'AM'; break;
                    case 'Afternoon': leaveLabel = 'PM'; break;
                    case 'Full': leaveLabel = 'Holiday'; break;
                    default: leaveLabel = leaveType || "";
                }

                const holidayName = this.getCompanyHolidayName(dayStr);
                const holidayPeriod = this.companyHolidayPeriodByDate?.[dayStr];
                if (!holidayName) return leaveLabel;
                if (holidayPeriod === "HOLIDAY") return holidayName;
                if (holidayPeriod === "HALF_HOLIDAY") {
                    const base = `${holidayName} (Company Half Holiday)`;
                    return leaveLabel ? `${base} / ${leaveLabel}` : base;
                }
                return leaveLabel;
            },
            hasSeatReservation(userId, dayStr) {
                return this.seatReservations.some(
                    (res) => res.user === userId && res.date === dayStr && !res.is_guest
                    );
            },
            getSeatReservation(userId, dayStr) {
                return this.seatReservations.find(
                    (res) => res.user === userId && res.date === dayStr && !res.is_guest
                    );
            },
            async fetchSeatReservations() {
                try {
                    const response = await api.get("/seats/seat-reservations/calendar/", {
                        params: {
                            month: `${this.selectedYear}-${String(this.selectedMonth).padStart(
                                2,
                                "0"
                            )}`,
                        },
                    });
                    this.seatReservations = response.data.reservations;
                } catch (error) {
                    console.error(
                        "Failed to fetch seat reservation information:",
                        error.response ? error.response.data : error
                        );
                }
            },
            async fetchHolidaysData() {
                try {
                    const resp = await fetchHolidays();
                    const rows = Array.isArray(resp.data) ? resp.data : [];
                    this.ukHolidays = rows
                      .filter((item) => item.period !== "HALF_HOLIDAY")
                      .map((item) => item.date);
                    this.halfHolidays = rows
                      .filter((item) => item.period === "HALF_HOLIDAY")
                      .map((item) => item.date);
                    const nameMap = {};
                    const periodMap = {};
                    rows
                      .filter((item) => item.holiday_source === "COMPANY")
                      .forEach((item) => {
                        nameMap[item.date] = item.name || "Company Holiday";
                        periodMap[item.date] = item.period;
                      });
                    this.companyHolidayNameByDate = nameMap;
                    this.companyHolidayPeriodByDate = periodMap;
                } catch (e) {
                    console.error("Failed to fetch holidays", e);
                }
            },
            async fetchAnnualOvertime() {
                const fy =
                this.selectedMonth >= 4 ? this.selectedYear : this.selectedYear - 1;
                try {
                    const res = await api.get("/annual-overtimes/", {
                        params: {
                            fiscal_year: fy,
                            user: this.currentUserId || undefined,
                            _ts: Date.now(),
                        },
                    });
                    this.annualOvertime = res.data.length ? res.data[0] : null;
                } catch (e) {
                    console.error("Failed to fetch annual overtime summary", e);
                    this.annualOvertime = null;
                }
            },
            getReferenceDateParam() {
                const monthEnd = new Date(this.selectedYear, this.selectedMonth, 0);
                const today = this.$now();
                const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                const refDate = monthEnd > normalizedToday ? normalizedToday : monthEnd;
                const yyyy = refDate.getFullYear();
                const mm = String(refDate.getMonth() + 1).padStart(2, "0");
                const dd = String(refDate.getDate()).padStart(2, "0");
                return `${yyyy}-${mm}-${dd}`;
            },
            async fetchSeventeenWeekAverage() {
                try {
                    const res = await api.get("/attendance/overtime-17week-average/", {
                        params: {
                            user_id: this.currentUserId,
                            reference_date: this.getReferenceDateParam(),
                        },
                    });
                    this.seventeenWeekAverageMinutes = res.data.average_overtime_minutes || 0;
                    this.seventeenWeekDivisorWeeks = res.data.divisor_weeks || 17;
                    this.isSeventeenWeekPartial = !!res.data.is_partial_period;
                } catch (e) {
                    console.error("Failed to fetch 17-week average overtime", e);
                    this.seventeenWeekAverageMinutes = 0;
                    this.seventeenWeekDivisorWeeks = 17;
                    this.isSeventeenWeekPartial = false;
                }
            },
            async prevMonth() {
                if (this.selectedMonth === 1) {
                    this.selectedMonth = 12;
                    this.selectedYear--;
                } else {
                    this.selectedMonth--;
                }
                await this.fetchAnnualOvertime();
                await this.fetchScheduleData();
                this.fetchSeatReservations();
                this.fetchAttendance(); // fetchAttendance を呼び出す
                this.fetchMonthlyAttendanceStatus();
                await this.fetchMonthlyFlexiTime();
                await this.fetchSeventeenWeekAverage();
            },
            async nextMonth() {
                if (this.selectedMonth === 12) {
                    this.selectedMonth = 1;
                    this.selectedYear++;
                } else {
                    this.selectedMonth++;
                }
                await this.fetchAnnualOvertime();
                await this.fetchScheduleData();
                this.fetchSeatReservations();
                this.fetchAttendance();
                this.fetchMonthlyAttendanceStatus();
                await this.fetchMonthlyFlexiTime();
                await this.fetchSeventeenWeekAverage();
            },
            goToReserveView(day) {
                this.$router.push({ path: "/attendance/deskreservation", query: { date: day } });
            },
            canReserve(day) {
                //予約可能なのは今日から30日間のみ
                const targetDate = new Date(day);
                const today = this.$now();
                const todayZero = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate()
                    );

                const yesterday = new Date(todayZero);
                yesterday.setDate(yesterday.getDate());

                const limitDate = new Date(todayZero);
                limitDate.setDate(limitDate.getDate() + 30);

                return targetDate >= yesterday && targetDate < limitDate;
            },
            isToday(day) {
                const targetDate = new Date(day);
                const today = this.$now();
                return targetDate.toDateString() === today.toDateString();
            },
            isFuture(day) {
                const targetDate = new Date(day);
                const today = this.$now();
            // 両方の日付をその日の始まりに正規化して比較
                targetDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                return targetDate > today;
            },
            isSaturday(day) {
                const d = new Date(day);
                return d.getDay() === 6;
            },
            isSunday(day) {
                const d = new Date(day);
                return d.getDay() === 0;
            },
            isUKHoliday(dayStr) {
                if (!dayStr) return false;
                const d = new Date(dayStr);
            if (isNaN(d.getTime())) return false; // 無効な日付はすぐ false に
            const dateStr = d.toISOString().slice(0, 10);
            return this.ukHolidays.includes(dateStr);
            },
            getAvailableSeatsCount(dayStr) {
                const reservedCount = this.seatReservations.filter(
                    (r) => r.date === dayStr
                    ).length;
                const totalSeats =
                    dayStr >= NEW_SEAT_LAYOUT_START ? NEW_LAYOUT_TOTAL_SEATS : this.totalSeats;
                return totalSeats - reservedCount;
            },
            async fetchTotalSeats() {
                try {
                    const res = await fetchSeatLabels();
                    this.totalSeats = res.data.length;
                } catch (e) {
                    console.error("Failed to fetch seat labels", e);
                }
            },

            async toggleMonthClosed() {
                const userId = this.currentUserId;
                const year = this.selectedYear;
                const month = this.selectedMonth;
                const newStatus = !this.isMonthClosed;

                try {
                    await api.post("/attendance/monthly-status/", {
                        user_id: userId,
                        year: year,
                        month: month,
                        is_closed: newStatus,
                    });
                    this.isMonthClosed = newStatus;
                    // Re-sync values derived from close/open state (annual overtime and flexitime)
                    await this.fetchMonthlyAttendanceStatus();
                    await this.fetchAnnualOvertime();
                    await this.fetchMonthlyFlexiTime();
                    alert(`Monthly attendance ${newStatus ? "closed" : "unlocked"}.`);
                } catch (error) {
                    console.error("Failed to update month closing status:", error);
                    alert(
                        "Failed to update month closing status. Please check the console for details."
                        );
            // Revert the UI change on failure
                    this.isMonthClosed = !newStatus;
                }
            },
            async fetchMonthlyAttendanceStatus() {
                const userId = this.currentUserId;
                const year = this.selectedYear;
                const month = this.selectedMonth;

                try {
                    const response = await api.get("/attendance/monthly-status/", {
                        params: {
                            user_id: userId,
                            year: year,
                            month: month,
                            _ts: Date.now(),
                        },
                    });
                    this.isMonthClosed = response.data.is_closed;
                } catch (error) {
                    console.error("Failed to fetch month closing status:", error);
                    this.isMonthClosed = false; // エラー時は開いているとみなす
                }
            },
            async fetchMonthlyFlexiTime() {
                const fiscal_year = this.selectedMonth >= 4 ? this.selectedYear : this.selectedYear - 1;
                try {
                    const response = await api.get('/attendance/flexitime/', {
                        params: {
                            user_id: this.currentUserId,
                            fiscal_year: fiscal_year,
                            month: this.selectedMonth,
                            _ts: Date.now(),
                        },
                    });
                    this.flexiHours = response.data.minutes ? (response.data.minutes / 60) : 0;
                } catch (error) {
                    console.error('Failed to fetch monthly flexitime:', error);
                    this.flexiHours = 0;
                }
            },
            async fetchApprovers() {
                // Only fetch if the list is empty to prevent multiple API calls.
                if (this.approverList && this.approverList.length > 0) {
                    return;
                }
                try {
                    const response = await api.get('accounts/approvers/');
                    // Ensure approverList is always an array
                    this.approverList = response.data || [];
                } catch (error) {
                    console.error('Failed to fetch approvers:', error);
                    // On error, ensure it's an empty array to prevent further errors
                    this.approverList = [];
                }
            },
            async fetchUserProfile() {
                try {
                    const res = await fetchProfile();
                    this.isExpat = res.data.employment_type === 'Expat';
                } catch (e) {
                    console.error("Failed to fetch profile", e);
                    this.isExpat = false;
                }
            },
            async refreshMonthlyState() {
                await this.fetchMonthlyAttendanceStatus();
                await this.fetchAnnualOvertime();
                await this.fetchMonthlyFlexiTime();
                await this.fetchSeventeenWeekAverage();
            },
        },
        async mounted() {
            const token = sessionStorage.getItem("token");
            if (token) {
                setAuthToken(token);
            } else {
                console.error("Authentication token not found. Please log in.");
            }
            await this.fetchApprovers();
            this.isAttendanceAdmin =
            localStorage.getItem("isAttendanceAdmin") === "true"; // 初期化
            this.isTopLevel = localStorage.getItem("isTopLevel") === "true"; // isTopLevel を初期化
            await this.fetchUserProfile();
            await this.fetchScheduleData();
            this.fetchSeatReservations();
            this.fetchHolidaysData();
            this.fetchAttendance();
            this.fetchCompBalance();
            await this.refreshMonthlyState();
            this.fetchTotalSeats();
            this._onWindowFocusRefresh = () => {
                this.refreshMonthlyState();
            };
            window.addEventListener("focus", this._onWindowFocusRefresh);
        },
        beforeUnmount() {
            if (this._onWindowFocusRefresh) {
                window.removeEventListener("focus", this._onWindowFocusRefresh);
            }
        },
    };
</script>

<style scoped>
    .flexi-time-section {
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid #ccc;
    }
    .form-group {
        margin: 10px 0;
    }
    .month-nav {
        margin-bottom: 1rem;
    }
    .tableWrap{
      overflow: auto;
      height: 70vh;
    }
    table {
        border-collapse: collapse;
        width: 100%;
    }
    th {
        padding: 8px;
        text-align: center;
        color: white;
        font-size: 13px;
        height: 2rem;
        min-width: 50px;
        &.bgBlu {
            background: var(--blue);
            &:first-child {
                border-top-left-radius: 10px;
            }
            &:last-child {
                border-top-right-radius: 10px;
            }
        }
        &.bgGre {
            background: var(--green);
            &:last-child {
                border-top-right-radius: 10px;
            }
        }
        &.bgGuest {
            background: #3ca679;
            &:last-child {
                border-top-right-radius: 10px;
            }
        }
    }
    td {
        border: 1px dashed #0757a280;
        padding: 8px;
        text-align: center;
        & input,
        select {
            border: none;
        }
        &:first-child {
            border-left: none;
        }
        &:last-child {
            border-right: none;
        }
    }
    
    .noWrapFlex{
        white-space: nowrap;
    }
    tr {
        &:first-child > td {
            border-top: none;
        }
        & > td:first-child {
            border-left: none;
            color: var(--blue);
            font-weight: 600;
            text-align: left;
            position: sticky;
            left: 0;
            background: white;
            z-index: 10;
        }
        &.today {
            background: var(--gradYel);
            & > td:first-child{ background: linear-gradient(-90deg, #cde8ff, #cdf4ff); }
            & input,
            & select {
                background: none;
            }
            .not-working-indicator {
                position: absolute;
                top: 5px;
                right: 5px;
                width: 14px;
                height: 14px;
                border: dashed 1px #eaea7c;
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 10px;
                font-weight: bold;
                color: #eaea7c;
                z-index: 10;

                &::before {
                    content: "!";
                }
            }
        }
        &.saturday,
        &.sunday,
        &.uk-holiday {
            background: #e9e9e9;
        }
        & .overTime, & .location {
            color: gray;
            font-size: 14px;
        }
    }
    .days {
        font-size: 12px;
    }
    thead {
        z-index: 2;
        position: sticky;
        top: 0;
    }
    tbody{
      position: relative;
      z-index: 1;
    }
    .summaryData {
        display: flex;

        & > div {
            width: 150px;
            border: 1px solid var(--lightBlue);
            border-radius: 10px;
            text-align: center;
            margin-right: 1rem;
            & p {
                color: var(--blue);
                font-weight: 600;
                font-size: 14px;

                &:first-child {
                    margin: 0;
                    padding: 0.6rem 0;
                    border-top-right-radius: 10px;
                    border-top-left-radius: 10px;
                    background: var(--lightBlue);
                }
            }
        }
    }
    .summaryNote {
        margin: 0.5rem 0 0;
        color: var(--blue);
        font-size: 12px;
    }

    .teamWrap {
        margin-right: 1rem;
        & label {
            color: var(--blue);
            font-weight: 600;
            margin-right: 0.5rem;
        }
        & select {
            color: var(--blue);
            border-color: var(--blue);
            padding: 0.4rem;
            border-radius: 10px;
        }
    }

    .seat-cell {
        position: relative;
        height: 40px;
        width: 40px;

        & .reserveLink {
            color: var(--blue);
            border: 1px solid;
            border-radius: 5px;
            width: 100%;
            height: 100%;
            display: block;
            align-content: center;
            font-size: 12px;
            padding: 0.3rem 0;
            margin-top: 0.1rem;
        }

        &::before {
            content: attr(data-leave);
            position: absolute;
            left: 0;
            width: 100%;
            background: #e9e9e9;
            font-size: 0.85em;
            white-space: nowrap;
            text-align: center;
            line-height: 1.2;
        }

        &[data-leave="Full"] {
            & .reservation {
                align-content: center;
                font-size: 12px;
                color: gray;
                top: 50%;
                transform: translate(-50%, -50%);
            }
            &::before {
                top: 0;
                height: 100%;
                color: gray;
                align-content: center;
            }
        }
        &[data-leave="AM"] {
            &::before {
                align-content: center;
                font-size: 12px;
                color: gray;
                top: 0;
                height: 50%;
            }
            & .reservation {
                bottom: 5%;
                height: 50%;
                transform: translate(-50%, -5%);
            }
            & .reserveLink{
              bottom: 5%;
              height: 50%;
              transform: translate(0, 50%);
              border-radius: 5px;
              margin-bottom: -5px;
            }
        }
        &[data-leave="PM"] {
            &::before {
                align-content: center;
                font-size: 12px;
                color: gray;
                bottom: 0;
                height: 50%;
            }
            & .reservation {
                top: 5%;
                height: 50%;
                transform: translate(-50%, 0);
            }
            & .reserveLink{
              top: 5%;
              height: 50%;
              transform: translate(0, -50%);
              border-radius: 5px;
              margin-bottom: 5px;
            }
        }
    }

    

/* ---- 席番号（.reservation）の配置切り替え ---- */
.reservation {
    position: absolute;
    width: 90%;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    height: 90%;
    border-radius: 5px;
    &.bgBlu a,
    &.bgGre {
        color: white;
    }
    &.bgGuest {
        color: white;
        background: #3ca679;
    }
}

@media (max-width: 768px) {
    .weekendSwc {
        margin: 2rem 0 1rem;
    }
    .teamWrap {
        margin: 0;
    }
}
.submit-button {
    background: var(--grad90);
    width: 100%;
    max-width: 300px;
    padding: 1rem;
    border: none;
    color: white;
    border-radius: 10px;
    margin: 20px auto;
    display: block;
}
.submit-button:disabled {
    background: #cccccc;
    cursor: inherit;
}
#flexitime-hours {
    padding: 0.5rem;
    font-size: 1.1em;
    margin-left: 0.5rem;
}
</style>
